import React, { useCallback } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingSM, HeadingXL, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BoundedImage } from '@/molecules/BoundedImage'
import { LivestreamThumbnail } from '@/molecules/LivestreamThumbnail'
import { Livestream } from '@/services/LivestreamService'
import { ProjectMeta } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface WatchProjectLivestreamsViewProps {
  livestreams: Livestream[]
  projectMeta: ProjectMeta
}

export const WatchProjectLivestreamsView: React.FC<WatchProjectLivestreamsViewProps> = ({
  livestreams,
  projectMeta,
}) => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()

  const hasLivestreams = livestreams && livestreams.length > 0
  const titleLogo = projectMeta?.title && 'logo' in projectMeta.title ? projectMeta.title.logo : null
  const logoUrl =
    titleLogo && typeof titleLogo === 'object' && titleLogo !== null && 'cloudinaryPath' in titleLogo
      ? (titleLogo.cloudinaryPath as string)
      : projectMeta?.logoCloudinaryPath
  const projectName = projectMeta?.name

  const handleLivestreamClick = useCallback(
    (livestream: Livestream) => {
      track('Watch Livestreams Page Livestream Video Clicked', {
        program: livestream.projectSlug,
        asset_id: livestream.guid,
        title: livestream.name,
      })
    },
    [track],
  )

  const handleCtaClick = useCallback(() => {
    track('Watch Livestreams Page CTA Clicked', {
      program: projectMeta?.slug,
      title: projectMeta?.name,
    })
  }, [track, projectMeta])

  return (
    <div className="min-h-screen bg-core-gray-950 pb-20 pt-28 text-white">
      <PaddedContainer fluid>
        <div className="mb-12">
          {logoUrl && (
            <BoundedImage
              className="mb-2 flex items-center justify-center md:justify-start lg:mb-4"
              alt={projectMeta?.name}
              path={logoUrl}
              width={600}
              height={104}
            />
          )}
          <HeadingXL weight="bold" className="mb-4">
            {t('livestreams', 'Livestreams')}
          </HeadingXL>
          <ParagraphMD color="core-gray-500">
            <Translate t={t} i18nKey="watchProjectLivestreamsDescription" values={{ projectName }}>
              Tune in to all <i>{{ projectName }}</i> livestreams — from exclusive premieres and cast interviews to live
              events and giveaways.
            </Translate>
          </ParagraphMD>
        </div>

        <When condition={!hasLivestreams}>
          <div className="py-20 text-left">
            <HeadingSM weight="semibold" className="mb-4">
              {t('noLivestreamsAvailable', 'No livestreams available at this time')}
            </HeadingSM>
            <ParagraphSM color="core-gray-400">
              {t('checkBackLater', 'Check back later for upcoming livestreams')}
            </ParagraphSM>
          </div>
        </When>

        <When condition={hasLivestreams}>
          <LazyHydrate whenVisible>
            <div className="flex flex-row flex-wrap gap-x-6 gap-y-8">
              {livestreams?.map((livestream) => (
                <div
                  key={livestream?.guid}
                  className="min-w-full md:min-h-[180px] md:min-w-[320px]"
                  onClick={() => handleLivestreamClick(livestream)}
                >
                  <LivestreamThumbnail {...livestream} />
                </div>
              ))}
            </div>
          </LazyHydrate>
        </When>
        <LinkButton
          onClick={handleCtaClick}
          href={`${paths.watch.index}/${projectMeta.slug}`}
          className="mt-12 w-full border-transparent bg-core-gray-800 hover:bg-core-gray-800/50 md:w-fit"
          variant={'core-gray-700'}
        >
          {t('watchProject', 'Watch {{ projectName }}', { projectName: projectMeta.name })}
        </LinkButton>
      </PaddedContainer>
    </div>
  )
}
